import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api';
import { analyticsAddToCart } from '../utils/analytics';

const CartContext = createContext();

const FREE_GIFT_KEY_PREFIX = 'free-gift-tier-';

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem('yellove_cart') || '[]'); } catch { return []; }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [freeTiers, setFreeTiers] = useState([]);
  const [freeShippingAbove, setFreeShippingAbove] = useState(null);
  const [activeDeals, setActiveDeals] = useState([]); // { product_id, deal_price }
  const [logoUrl, setLogoUrl] = useState('');
  const [logoHeightDesktop, setLogoHeightDesktop] = useState(96);
  const [logoHeightMobile, setLogoHeightMobile] = useState(62);
  const tiersLoadedRef = useRef(false);

  useEffect(() => {
    localStorage.setItem('yellove_cart', JSON.stringify(cart));
  }, [cart]);

  // Load settings + free gift tiers once
  useEffect(() => {
    if (tiersLoadedRef.current) return;
    tiersLoadedRef.current = true;
    api.get('/free-gift-tiers/active').then(({ data }) => {
      setFreeTiers(data.tiers || []);
    }).catch(() => {});
    api.get('/flash-deals/active').then(({ data }) => {
      setActiveDeals(data.deals || []);
    }).catch(() => {});
    api.get('/settings/public').then(({ data }) => {
      const v = data.settings?.free_shipping_above;
      if (v != null && v !== '') setFreeShippingAbove(parseFloat(v));
      if (data.settings?.logo_url) setLogoUrl(data.settings.logo_url);
      if (data.settings?.logo_height_desktop) setLogoHeightDesktop(parseInt(data.settings.logo_height_desktop));
      if (data.settings?.logo_height_mobile) setLogoHeightMobile(parseInt(data.settings.logo_height_mobile));
    }).catch(() => {});
  }, []);

  const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);
  const cartTotal = cart.filter(i => !i.isFreeGift).reduce((sum, item) => sum + item.price * item.qty, 0);
  // If any purchased product is flagged free_shipping, the whole order ships free.
  const cartHasFreeShipping = cart.some(i => !i.isFreeGift && i.free_shipping);

  const addToCart = (product, variant = null, qty = 1, overridePrice = null) => {
    const key = `${product.id}-${variant?.id || 'default'}`;
    api.post(`/products/${product.id}/cart-add`).catch(() => {});
    // Meta Pixel — AddToCart (fires here so every entry point is covered)
    {
      const mrp = parseFloat(variant?.price) || parseFloat(product.variants?.[0]?.price) || 0;
      const price = overridePrice != null
        ? parseFloat(overridePrice)
        : (parseFloat(variant?.discount_price) || mrp);
      analyticsAddToCart({
        id: product.id,
        name: variant?.title || product.name,
        price, qty,
        variant: variant ? [variant.color, variant.size, variant.model_name].filter(Boolean).join(' / ') : undefined,
      });
    }
    setCart(prev => {
      const existing = prev.find(i => i.key === key);
      if (existing) {
        return prev.map(i => i.key === key ? { ...i, qty: i.qty + qty } : i);
      }
      const mrp = parseFloat(variant?.price) || parseFloat(product.variants?.[0]?.price) || 0;
      const price = overridePrice != null
        ? parseFloat(overridePrice)
        : (parseFloat(variant?.discount_price) || mrp);
      // Use the SELECTED variant's own image (thumbnail first, then any variant image)
      // so the cart shows the variant the user actually chose. Falls back to the
      // product's default image when the variant has no image of its own.
      const variantImage = variant?.id
        ? (product.images?.find(im => im.variant_id === variant.id && im.is_thumbnail)?.image_url
           || product.images?.find(im => im.variant_id === variant.id)?.image_url)
        : null;
      return [...prev, {
        key,
        product_id: product.id,
        variant_id: variant?.id || null,
        // Prefer the variant's own title (e.g. "... (Red)") so the cart shows the
        // full, distinguishing name — variants often differ only by title, not
        // by color/size, so the base product name alone looks identical.
        name: variant?.title || product.name,
        slug: product.slug,
        image: variantImage || product.images?.[0]?.image_url || null,
        price,
        mrp,
        weight: parseFloat(product.weight) || 500,
        variant_label: variant ? [variant.color, variant.size, variant.model_name].filter(Boolean).join(' / ') : null,
        free_shipping: !!product.free_shipping,
        qty,
      }];
    });
  };

  const updateQty = (key, qty) => {
    if (qty <= 0) return removeFromCart(key);
    setCart(prev => prev.map(i => i.key === key ? { ...i, qty } : i));
  };

  const removeFromCart = (key) => setCart(prev => prev.filter(i => i.key !== key));

  const clearCart = () => setCart([]);

  // Sync free gifts whenever cartTotal or freeTiers change
  const syncFreeGifts = useCallback(() => {
    if (freeTiers.length === 0) return;

    setCart(prev => {
      let updated = prev.filter(i => !i.isFreeGift); // strip all old free gifts

      const highestTier = freeTiers
        .filter(t => cartTotal >= parseFloat(t.min_amount))
        .sort((a, b) => parseFloat(b.min_amount) - parseFloat(a.min_amount))[0];
      const unlockedTiers = highestTier ? [highestTier] : [];

      const newGifts = unlockedTiers.map(tier => {
        const variant = tier.variant || tier.product?.variants?.[0];
        const key = `${FREE_GIFT_KEY_PREFIX}${tier.id}`;
        return {
          key,
          product_id: tier.product_id,
          variant_id: variant?.id || null,
          name: tier.product?.name || 'Free Gift',
          slug: tier.product?.slug || '',
          image: tier.product?.images?.[0]?.image_url || null,
          price: 0,
          mrp: parseFloat(variant?.price) || 0,
          weight: 0,
          variant_label: variant ? [variant.color, variant.size].filter(Boolean).join(' / ') : null,
          qty: 1,
          isFreeGift: true,
          tierMinAmount: parseFloat(tier.min_amount),
        };
      });

      // Only update if something actually changed
      const prevGifts = prev.filter(i => i.isFreeGift);
      const same = prevGifts.length === newGifts.length &&
        newGifts.every(ng => prevGifts.some(pg => pg.key === ng.key));
      if (same) return prev;

      return [...updated, ...newGifts];
    });
  }, [freeTiers, cartTotal]);

  useEffect(() => {
    syncFreeGifts();
  }, [syncFreeGifts]);

  return (
    <CartContext.Provider value={{
      cart, cartCount, cartTotal, cartHasFreeShipping, freeTiers, freeShippingAbove, activeDeals, logoUrl, logoHeightDesktop, logoHeightMobile,
      addToCart, updateQty, removeFromCart, clearCart,
      drawerOpen, setDrawerOpen,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
